/* eslint-disable react/no-unescaped-entities */
import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth, formatApiError } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { useTrackModuleView, useTrackSearch } from "@/lib/activityTracker";
import LangSwitch from "@/components/LangSwitch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Plus,
  MoreVertical,
  ArrowLeft,
  Search,
  Stethoscope,
  Syringe,
  Pill,
  HeartPulse,
  ClipboardList,
  LogOut,
  AlertTriangle,
  CalendarClock,
  FileDown,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_META = {
  vaccine: { label: "Vaccin", icon: Syringe, color: "#1E6BBA", bg: "bg-blue-100", text: "text-blue-800" },
  treatment: { label: "Traitement", icon: Pill, color: "#C84B31", bg: "bg-orange-100", text: "text-orange-800" },
  checkup: { label: "Visite vétérinaire", icon: Stethoscope, color: "#1C7A3C", bg: "bg-emerald-100", text: "text-emerald-800" },
  disease: { label: "Maladie", icon: HeartPulse, color: "#8A1F1F", bg: "bg-red-100", text: "text-red-800" },
  other: { label: "Autre", icon: ClipboardList, color: "#5C6B5C", bg: "bg-zinc-200", text: "text-zinc-700" },
};

const TYPES = Object.keys(TYPE_META);
const empty = {
  animal_id: "",
  type: "vaccine",
  title: "",
  product: "",
  dose: "",
  vet_name: "",
  date: new Date().toISOString().slice(0, 10),
  next_due_date: "",
  cost: "",
  notes: "",
};

export default function HealthRecords() {
  const { user, loading, logout } = useAuth();
  const { t, lang } = useLang();
  const location = useLocation();
  const [data, setData] = useState({ records: [], animals: [], stats: { total: 0, by_type: {} } });
  const [reminders, setReminders] = useState({ upcoming: [], overdue: [] });
  const [loadingData, setLoadingData] = useState(true);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterAnimal, setFilterAnimal] = useState("all");
  const [openCreate, setOpenCreate] = useState(false);

  useTrackModuleView("health");
  useTrackSearch("health", search);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [allAnimals, setAllAnimals] = useState([]);
  const [exporting, setExporting] = useState(false);

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const qs = new URLSearchParams({ lang });
      if (filterAnimal !== "all") qs.append("animal_id", filterAnimal);
      const res = await axios.get(`${API}/health-records/export.pdf?${qs.toString()}`, {
        withCredentials: true,
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const suffix = filterAnimal === "all"
        ? "complet"
        : (allAnimals.find((x) => x.id === filterAnimal)?.tag || "animal");
      a.download = `carnet-sanitaire-${suffix}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t.app.health.pdf_generated);
    } catch (e) {
      toast.error(t.app.health.export_error, {
        description: formatApiError(e?.response?.data?.detail || "—"),
      });
    } finally {
      setExporting(false);
    }
  };

  const fetchAll = async () => {
    setLoadingData(true);
    try {
      const [recs, anims, rems] = await Promise.all([
        axios.get(`${API}/health-records`, { withCredentials: true }),
        axios.get(`${API}/animals`, { withCredentials: true }),
        axios.get(`${API}/health-records/reminders`, { withCredentials: true }),
      ]);
      setData(recs.data);
      setAllAnimals(anims.data.animals || []);
      setReminders(rems.data);
    } catch (e) {
      toast.error(t.toasts.error_title, { description: formatApiError(e?.response?.data?.detail) });
    } finally {
      setLoadingData(false);
    }
  };

  const animalsById = useMemo(() => {
    const map = {};
    [...allAnimals, ...(data.animals || [])].forEach((a) => {
      if (a) map[a.id] = a;
    });
    return map;
  }, [allAnimals, data.animals]);

  useEffect(() => {
    if (user && user.role === "client" && user.status === "active") fetchAll();
  }, [user]);

  if (loading)
    return (
      <div className="min-h-screen grid place-items-center bg-[#FAFAF7] text-[#5C6B5C]">
        {t.app.common.loading}
      </div>
    );
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  if (user.role !== "client") return <Navigate to="/" replace />;
  if (user.status !== "active") return <Navigate to="/dashboard/client" replace />;

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/health-records/${deleteTarget.id}`, { withCredentials: true });
      toast.success(t.app.health.deleted_toast);
      setDeleteTarget(null);
      fetchAll();
    } catch (e) {
      toast.error(t.toasts.error_title, { description: formatApiError(e?.response?.data?.detail) });
    }
  };

  const filtered = (data.records || []).filter((r) => {
    if (filterType !== "all" && r.type !== filterType) return false;
    if (filterAnimal !== "all" && r.animal_id !== filterAnimal) return false;
    const q = search.toLowerCase();
    if (!q) return true;
    const an = animalsById[r.animal_id];
    return (
      r.title?.toLowerCase().includes(q) ||
      r.product?.toLowerCase().includes(q) ||
      r.vet_name?.toLowerCase().includes(q) ||
      an?.tag?.toLowerCase().includes(q) ||
      an?.name?.toLowerCase().includes(q)
    );
  });

  const hasAnimals = allAnimals.length > 0;

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <header className="bg-[#1C3A27] text-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard/client" className="flex items-center gap-2" data-testid="back-to-dashboard">
            <ArrowLeft size={18} className="text-white/70" />
            <span className="font-display text-lg font-bold">Mazra3ti</span>
          </Link>
          <Button onClick={logout} variant="ghost" size="sm" className="text-white/80" data-testid="health-logout">
            <LogOut size={16} className="me-2" /> Déconnexion
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.2em] text-[#E05A3D]">
              <Stethoscope size={14} /> Santé du troupeau
            </div>
            <h1 className="mt-2 font-display text-3xl md:text-4xl font-black text-[#1C3A27]">
              Carnet sanitaire
            </h1>
            <p className="mt-1 text-[#5C6B5C]">
              Vaccins, traitements, visites vétérinaires et rappels — pour chaque animal.
            </p>
          </div>
          {hasAnimals ? (
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={handleExportPdf}
                variant="outline"
                data-testid="export-pdf"
                disabled={exporting || allAnimals.length === 0}
                className="h-12 px-5 rounded-xl border-[#1C3A27] text-[#1C3A27] hover:bg-[#1C3A27] hover:text-white font-semibold"
              >
                <FileDown size={18} className="me-2" />
                {exporting ? "Génération…" : filterAnimal === "all" ? "Exporter PDF (tous)" : "Exporter PDF"}
              </Button>
              <Dialog open={openCreate} onOpenChange={setOpenCreate}>
              <DialogTrigger asChild>
                <Button
                  data-testid="open-create-health"
                  className="h-12 px-5 rounded-xl bg-[#E05A3D] hover:bg-[#C84B31] text-white font-semibold"
                >
                  <Plus size={18} className="me-2" /> Nouvel enregistrement
                </Button>
              </DialogTrigger>
              <HealthDialogContent
                title="Ajouter au carnet sanitaire"
                animals={allAnimals}
                onClose={() => setOpenCreate(false)}
                onSaved={() => {
                  setOpenCreate(false);
                  fetchAll();
                }}
              />
            </Dialog>
            </div>
          ) : (
            <Link to="/dashboard/client/animals">
              <Button className="h-12 px-5 rounded-xl bg-[#1C3A27] hover:bg-[#142A1C] text-white font-semibold">
                Ajouter un animal d'abord
              </Button>
            </Link>
          )}
        </div>

        {/* Reminders */}
        {(reminders.overdue?.length > 0 || reminders.upcoming?.length > 0) && (
          <div className="grid md:grid-cols-2 gap-5 mb-8">
            {reminders.overdue?.length > 0 && (
              <ReminderBlock
                tone="danger"
                icon={AlertTriangle}
                title={`${t.app.health.reminder_overdue} (${reminders.overdue.length})`}
                items={reminders.overdue}
                t={t}
              />
            )}
            {reminders.upcoming?.length > 0 && (
              <ReminderBlock
                tone="info"
                icon={CalendarClock}
                title={`${t.app.health.reminder_upcoming} (${reminders.upcoming.length})`}
                items={reminders.upcoming}
                t={t}
              />
            )}
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-8">
          <StatCard label={t.app.health.stat_total} value={data.stats?.total || 0} />
          {TYPES.map((tp) => (
            <StatCard
              key={tp}
              label={t.app.health.types[tp]}
              value={data.stats?.by_type?.[tp] || 0}
              accent={TYPE_META[tp].color}
            />
          ))}
        </div>

        <div className="flex flex-wrap gap-3 mb-5">
          <div className="relative flex-1 min-w-[240px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6B5C]" />
            <Input
              data-testid="health-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t.app.health.search_placeholder}
              className="ps-10 h-11 bg-white border-[#E2E4DF] rounded-xl"
            />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-44 h-11 bg-white border-[#E2E4DF] rounded-xl" data-testid="filter-type">
              <SelectValue placeholder={t.app.health.th_type} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t.app.health.all_types}</SelectItem>
              {TYPES.map((tp) => (
                <SelectItem key={tp} value={tp}>{t.app.health.types[tp]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterAnimal} onValueChange={setFilterAnimal}>
            <SelectTrigger className="w-52 h-11 bg-white border-[#E2E4DF] rounded-xl" data-testid="filter-animal">
              <SelectValue placeholder={t.app.health.th_animal} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t.app.health.all_animals}</SelectItem>
              {allAnimals.map((a) => (
                <SelectItem key={a.id} value={a.id}>
                  {a.tag}{a.name ? ` — ${a.name}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="rounded-2xl border border-[#E2E4DF] bg-white overflow-hidden">
          {loadingData ? (
            <div className="p-10 text-center text-[#5C6B5C]">{t.app.common.loading}</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <Stethoscope size={40} className="mx-auto text-[#5C6B5C] opacity-40" />
              <p className="mt-3 text-[#5C6B5C]">
                {data.records.length === 0
                  ? hasAnimals
                    ? t.app.health.empty_first_with_animals
                    : t.app.health.empty_no_animals
                  : t.app.health.empty_search}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[800px]">
                <thead className="bg-[#F0F0EB] text-[#5C6B5C] text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-start px-5 py-3 font-semibold">{t.app.health.th_date}</th>
                    <th className="text-start px-5 py-3 font-semibold">{t.app.health.th_animal}</th>
                    <th className="text-start px-5 py-3 font-semibold">{t.app.health.th_type}</th>
                    <th className="text-start px-5 py-3 font-semibold">{t.app.health.th_title}</th>
                    <th className="text-start px-5 py-3 font-semibold">{t.app.health.th_vet}</th>
                    <th className="text-start px-5 py-3 font-semibold">{t.app.health.th_next}</th>
                    <th className="text-start px-5 py-3 font-semibold">{t.app.health.th_cost}</th>
                    <th className="text-end px-5 py-3 font-semibold">{t.app.common.actions}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => {
                    const an = animalsById[r.animal_id];
                    const meta = TYPE_META[r.type] || TYPE_META.other;
                    const Icon = meta.icon;
                    return (
                      <tr
                        key={r.id}
                        data-testid={`health-row-${r.id}`}
                        className="border-t border-[#E2E4DF]"
                      >
                        <td className="px-5 py-4 text-[#1C3A27] font-medium">{r.date}</td>
                        <td className="px-5 py-4 text-[#1C3A27]">
                          {an ? (
                            <span className="font-mono font-semibold">{an.tag}</span>
                          ) : (
                            <span className="text-[#5C6B5C] italic">{t.app.health.animal_deleted}</span>
                          )}
                          {an?.name && <span className="text-xs text-[#5C6B5C]"> · {an.name}</span>}
                        </td>
                        <td className="px-5 py-4">
                          <span
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${meta.bg} ${meta.text}`}
                          >
                            <Icon size={12} /> {t.app.health.types[r.type]}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-[#1C3A27]">
                          <div className="font-medium">{r.title}</div>
                          {r.product && <div className="text-xs text-[#5C6B5C]">{r.product}{r.dose ? ` · ${r.dose}` : ""}</div>}
                        </td>
                        <td className="px-5 py-4 text-[#5C6B5C]">{r.vet_name || "—"}</td>
                        <td className="px-5 py-4 text-[#5C6B5C]">{r.next_due_date || "—"}</td>
                        <td className="px-5 py-4 text-[#5C6B5C]">
                          {r.cost != null ? `${r.cost} TND` : "—"}
                        </td>
                        <td className="px-5 py-4 text-end">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                data-testid={`health-actions-${r.id}`}
                                className="p-2 rounded-lg hover:bg-[#F0F0EB] text-[#5C6B5C]"
                              >
                                <MoreVertical size={18} />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => setEditTarget(r)}
                                data-testid={`edit-health-${r.id}`}
                              >
                                {t.app.common.edit}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => setDeleteTarget(r)}
                                data-testid={`delete-health-${r.id}`}
                                className="text-red-700"
                              >
                                {t.app.common.delete}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        {editTarget && (
          <HealthDialogContent
            title={t.app.health.edit_title}
            animals={allAnimals}
            initial={editTarget}
            onClose={() => setEditTarget(null)}
            onSaved={() => {
              setEditTarget(null);
              fetchAll();
            }}
          />
        )}
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t.app.health.delete_title}</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && t.app.health.delete_body.replace("{title}", deleteTarget.title).replace("{date}", deleteTarget.date)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t.app.common.cancel}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-health"
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              {t.app.common.delete}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StatCard({ label, value, accent = "#1C3A27" }) {
  return (
    <div className="rounded-2xl bg-white border border-[#E2E4DF] p-5">
      <div className="text-xs uppercase tracking-widest text-[#5C6B5C] font-semibold truncate">
        {label}
      </div>
      <div className="font-display text-3xl font-black mt-1" style={{ color: accent }}>
        {value}
      </div>
    </div>
  );
}

function ReminderBlock({ tone, icon: Icon, title, items, t }) {
  const cls =
    tone === "danger"
      ? "border-red-300 bg-red-50"
      : "border-amber-300 bg-amber-50";
  const iconCls = tone === "danger" ? "text-red-700" : "text-amber-700";
  const titleCls = tone === "danger" ? "text-red-900" : "text-amber-900";
  return (
    <div className={`rounded-2xl border-2 ${cls} p-5`} data-testid={`reminder-${tone}`}>
      <div className={`flex items-center gap-2 font-bold ${titleCls}`}>
        <Icon size={18} className={iconCls} /> {title}
      </div>
      <ul className="mt-3 space-y-2">
        {items.slice(0, 5).map((r) => {
          const meta = TYPE_META[r.type] || TYPE_META.other;
          return (
            <li key={r.id} className="flex items-center gap-3 text-sm">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${meta.bg} ${meta.text}`}>
                {t.app.health.types[r.type]}
              </span>
              <span className="font-mono font-semibold text-[#1C3A27]">
                {r.animal?.tag || "—"}
              </span>
              <span className="flex-1 text-[#1C3A27] truncate">{r.title}</span>
              <span className={`font-semibold ${tone === "danger" ? "text-red-700" : "text-amber-800"}`}>
                {r.next_due_date}
              </span>
            </li>
          );
        })}
        {items.length > 5 && (
          <li className="text-xs text-[#5C6B5C] italic">{t.app.health.reminder_more.replace("{n}", items.length - 5)}</li>
        )}
      </ul>
    </div>
  );
}

function HealthDialogContent({ title, animals, initial, onClose, onSaved }) {
  const { t } = useLang();
  const isEdit = !!initial;
  const [form, setForm] = useState(
    initial
      ? {
          animal_id: initial.animal_id,
          type: initial.type,
          title: initial.title,
          product: initial.product || "",
          dose: initial.dose || "",
          vet_name: initial.vet_name || "",
          date: initial.date,
          next_due_date: initial.next_due_date || "",
          cost: initial.cost ?? "",
          notes: initial.notes || "",
        }
      : { ...empty, animal_id: animals[0]?.id || "" }
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.animal_id) {
      setError(t.app.health.animal_required);
      return;
    }
    setSubmitting(true);
    const payload = {
      type: form.type,
      title: form.title.trim(),
      product: form.product.trim() || undefined,
      dose: form.dose.trim() || undefined,
      vet_name: form.vet_name.trim() || undefined,
      date: form.date,
      next_due_date: form.next_due_date || undefined,
      cost: form.cost === "" ? undefined : parseFloat(form.cost),
      notes: form.notes.trim() || undefined,
    };
    if (!isEdit) payload.animal_id = form.animal_id;

    try {
      if (isEdit) {
        await axios.patch(`${API}/health-records/${initial.id}`, payload, { withCredentials: true });
        toast.success("Enregistrement mis à jour");
      } else {
        await axios.post(`${API}/health-records`, payload, { withCredentials: true });
        toast.success("Enregistrement ajouté");
      }
      onSaved?.();
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogContent className="max-w-xl rounded-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-display text-2xl">{title}</DialogTitle>
      </DialogHeader>
      <form onSubmit={submit} data-testid="health-form" className="grid grid-cols-2 gap-4 mt-2">
        <div className="col-span-2">
          <Label>{t.app.health.animal_label}</Label>
          <Select
            value={form.animal_id}
            onValueChange={(v) => setForm({ ...form, animal_id: v })}
            disabled={isEdit}
          >
            <SelectTrigger className="mt-1.5 h-11 bg-white" data-testid="health-animal-select">
              <SelectValue placeholder={t.app.health.animal_select_ph} />
            </SelectTrigger>
            <SelectContent>
              {animals.map((a) => (
                <SelectItem key={a.id} value={a.id}>
                  {a.tag}{a.name ? ` — ${a.name}` : ""} · {t.app.animals.species[a.species] || a.species}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>{t.app.health.type_label}</Label>
          <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
            <SelectTrigger className="mt-1.5 h-11 bg-white" data-testid="health-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TYPES.map((tp) => (
                <SelectItem key={tp} value={tp}>{t.app.health.types[tp]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>{t.app.health.date_label}</Label>
          <Input
            type="date"
            required
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            className="mt-1.5 h-11 bg-white"
            data-testid="health-date"
          />
        </div>
        <div className="col-span-2">
          <Label>{t.app.health.title_label}</Label>
          <Input
            required
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder={t.app.health.title_ph}
            className="mt-1.5 h-11 bg-white"
            data-testid="health-title"
          />
        </div>
        <div>
          <Label>{t.app.health.product_label}</Label>
          <Input
            value={form.product}
            onChange={(e) => setForm({ ...form, product: e.target.value })}
            placeholder={t.app.health.product_ph}
            className="mt-1.5 h-11 bg-white"
          />
        </div>
        <div>
          <Label>{t.app.health.dose_label}</Label>
          <Input
            value={form.dose}
            onChange={(e) => setForm({ ...form, dose: e.target.value })}
            placeholder="5 ml"
            className="mt-1.5 h-11 bg-white"
          />
        </div>
        <div>
          <Label>{t.app.health.vet_label}</Label>
          <Input
            value={form.vet_name}
            onChange={(e) => setForm({ ...form, vet_name: e.target.value })}
            placeholder={t.app.health.vet_ph}
            className="mt-1.5 h-11 bg-white"
          />
        </div>
        <div>
          <Label>{t.app.health.cost_label}</Label>
          <Input
            type="number"
            min={0}
            step={0.01}
            value={form.cost}
            onChange={(e) => setForm({ ...form, cost: e.target.value })}
            className="mt-1.5 h-11 bg-white"
          />
        </div>
        <div className="col-span-2">
          <Label>{t.app.health.next_label}</Label>
          <Input
            type="date"
            value={form.next_due_date}
            onChange={(e) => setForm({ ...form, next_due_date: e.target.value })}
            className="mt-1.5 h-11 bg-white"
            data-testid="health-next-due"
          />
          <p className="text-xs text-[#5C6B5C] mt-1">
            {t.app.health.next_hint}
          </p>
        </div>
        <div className="col-span-2">
          <Label>{t.app.health.notes_label}</Label>
          <Textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            rows={3}
            className="mt-1.5 bg-white resize-none"
            placeholder={t.app.health.notes_ph}
          />
        </div>
        {error && (
          <div className="col-span-2 rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm p-3">
            {error}
          </div>
        )}
        <DialogFooter className="col-span-2 mt-1">
          <Button type="button" variant="outline" onClick={onClose}>{t.app.common.cancel}</Button>
          <Button
            type="submit"
            data-testid="submit-health"
            disabled={submitting}
            className="bg-[#1C3A27] hover:bg-[#142A1C] text-white"
          >
            {submitting ? t.app.common.saving : isEdit ? t.app.common.update : t.app.common.save}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
